import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalBrandingService } from '../../portal-branding.service';
import { PortalProfileService } from '../../portal-profile.service';

interface DialCode { code: string; label: string; }

// "Edit Profile" panel — a slide-in sidebar on desktop, a centred popup on mobile
// (see .ep-panel in edit-profile.component.scss). Mounted once at the app root and
// opened via PortalProfileService from any header. Saves profile details + an
// optional password change through portal_update_profile.
@Component({
	selector: 'portal-edit-profile',
	templateUrl: './edit-profile.component.html',
	styleUrls: ['./edit-profile.component.scss']
})
export class EditProfileComponent implements OnInit, OnDestroy {

	// Loaded once from the country_codes table (portal_dial_codes) the first time
	// the profile panel opens.
	dialCodes: DialCode[] = [];

	open = false;
	saving = false;
	error = '';

	firstName = '';
	lastName = '';
	dialCode = '+44';
	phone = '';
	email = '';

	currentPassword = '';
	newPassword = '';
	passwordConf = '';

	show = { current: false, new: false, conf: false };

	private sub: Subscription | null = null;

	constructor(
		private api: PortalApiService,
		public auth: PortalAuthService,
		public branding: PortalBrandingService,
		private profile: PortalProfileService
	) {}

	ngOnInit(): void {
		this.sub = this.profile.open$.subscribe(() => this.openPanel());
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}

	get avatarInitials(): string {
		const src = (this.firstName + ' ' + this.lastName).trim() || this.auth.user?.name || this.branding.companyName || '';
		const p = src.trim().split(/\s+/).filter(Boolean);
		return p.length ? (p[0][0] + (p[1]?.[0] || '')).toUpperCase() : '—';
	}

	openPanel(): void {
		const u = this.auth.user;
		if (!u) return;

		if (!this.dialCodes.length) this.loadDialCodes();

		// Prefer the stored first/last; fall back to splitting the display name.
		const parts = (u.name || '').trim().split(/\s+/).filter(Boolean);
		this.firstName = u.first_name || parts[0] || '';
		this.lastName = u.last_name || parts.slice(1).join(' ') || '';
		this.dialCode = u.phone_dial_code || '+44';
		this.phone = u.phone || '';
		this.email = u.email || '';

		this.currentPassword = '';
		this.newPassword = '';
		this.passwordConf = '';
		this.show = { current: false, new: false, conf: false };
		this.error = '';
		// The background scroll lock comes from the .ep-overlay.open rule in
		// styles.scss, so opening is all there is to do.
		this.open = true;
	}

	close(): void {
		this.open = false;
	}

	private loadDialCodes(): void {
		this.api.auth.dialCodes((res: any) => {
			this.dialCodes = res?.dial_codes || [];
		}, () => { /* leave empty on failure; the stored value still submits */ });
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (this.open) this.close();
	}

	submit(): void {
		this.error = '';
		if (!this.firstName.trim()) { this.error = 'Please enter your first name.'; return; }
		if (!this.email.trim()) { this.error = 'Please enter your email address.'; return; }

		if (this.newPassword || this.passwordConf || this.currentPassword) {
			if (this.newPassword.length < 7) { this.error = 'New password must be at least 7 characters long.'; return; }
			if (this.newPassword !== this.passwordConf) { this.error = 'Please enter the same new password twice to confirm.'; return; }
		}

		this.saving = true;
		this.api.auth.updateProfile({
			first_name: this.firstName.trim(),
			last_name: this.lastName.trim(),
			email: this.email.trim(),
			phone_dial_code: this.dialCode,
			phone: this.phone.trim(),
			current_password: this.currentPassword,
			new_password: this.newPassword,
			password_conf: this.passwordConf
		}, (res: any) => {
			this.saving = false;
			this.auth.setUser(res?.user || this.auth.user);
			this.close();
		}, (err: any) => {
			this.saving = false;
			// Session gone mid-action — sign in again and come back to this page,
			// rather than leaving them stuck on an inline error.
			if (err?.status === 401) { this.close(); this.auth.goToLogin(); return; }
			this.error = err?.message || 'We could not save your changes. Please try again.';
		});
	}
}
